import { playPcmChannels, stopPlayback } from "@/audio/playback";
import { BaseTtsAdapter, type TtsSpeakOptions } from "@/adapters/tts/base";
import { synthesizeWithMossTts } from "@/runtime/moss-tts-runtime";

/**
 * Local OpenMOSS TTS adapter. Runs on the main thread because the vendored
 * OpenMOSS browser runtime tokenizes via an iframe sandbox
 * (`/tokenizer_sandbox.html`), which requires a DOM — incompatible with a
 * dedicated Worker. Inference is ONNX/wasm; it's CPU-synchronous but not
 * tight enough to need its own worker for the current model sizes.
 *
 * If/when we need to move inference off the main thread, the approach is to
 * split moss-tts-runtime into a main-thread tokenizer (using the iframe) and
 * a worker-side inference helper that takes token IDs. That is a strictly
 * internal refactor inside this adapter — callers of TtsAdapter.speak() are
 * unaffected.
 */
export class LocalMossTtsAdapter extends BaseTtsAdapter {
  readonly id = "local-moss";

  async speak(text: string, options: TtsSpeakOptions = {}): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      this.publishDone();
      return;
    }

    const signal = options.abortSignal;
    let speech;
    try {
      speech = await synthesizeWithMossTts(trimmed, { abortSignal: signal });
    } catch (error) {
      if (signal?.aborted) {
        // Interruption is the caller's responsibility to observe; publish
        // a done so the UI phase machine exits the speaking state.
        this.publishDone();
        return;
      }
      const message = error instanceof Error ? error.message : "TTS synthesis failed";
      this.publishError(message);
      this.publishDone();
      throw error;
    }

    // Stream the chunks: publish the audio event for UI, then play the chunk.
    // Playback is sequential; interruption stops both.
    for (const chunk of speech.chunks) {
      if (signal?.aborted) {
        break;
      }

      this.publishAudio(chunk.chunkData[0] ?? new Float32Array(), chunk.sampleRate);
      try {
        await playPcmChannels(chunk.chunkData, chunk.sampleRate, { signal });
      } catch (error) {
        if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
          break;
        }
        throw error;
      }
    }

    if (!signal?.aborted) {
      this.publishDone();
    }
  }

  override async interrupt(): Promise<void> {
    stopPlayback();
  }
}
